import React from 'react'
import {
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  Skeleton,
  Tooltip,
} from '@mui/material'
import { Business } from '@mui/icons-material'
import styles from './DepartmentSelector.module.scss'
import { useDepartmentSelectorLogic } from './DepartmentSelector.logic'

interface DepartmentSelectorProps {
  variant?: 'navbar' | 'standard'
}

const DepartmentSelector: React.FC<DepartmentSelectorProps> = ({
  variant = 'standard',
}) => {
  const logic = useDepartmentSelectorLogic()
  if (logic.isLoading) {
    return (
      <Box className={styles.container}>
        {variant === 'navbar' && <Business className={styles.icon} />}
        <Skeleton
          className={`${styles.skeleton} ${
            variant === 'navbar'
              ? styles['skeleton--navbar']
              : styles['skeleton--standard']
          }`}
        />
      </Box>
    )
  }

  if (logic.error) {
    return (
      <Tooltip title={`Error loading departments: ${logic.error}`}>
        <Box className={styles.container}>
          {variant === 'navbar' && <Business className={styles.icon} />}
          <Typography
            variant="body2"
            className={`${styles.errorText} ${
              variant === 'navbar'
                ? styles['errorText--navbar']
                : styles['errorText--standard']
            }`}
          >
            Departments unavailable
          </Typography>
        </Box>
      </Tooltip>
    )
  }

  if (logic.departments?.length <= 1) {
    return null
  }

  return (
    <>
      <Box className={styles.container}>
        {variant === 'navbar' && <Business className={styles.icon} />}
        <FormControl
          size={variant === 'navbar' ? 'small' : 'medium'}
          className={`${styles.formControl} ${
            variant === 'navbar'
              ? styles['formControl--navbar']
              : styles['formControl--standard']
          }`}
        >
          <Select
            value={logic.selectedDepartment?.id}
            onChange={(e) =>
              logic.handleDepartmentChange(Number(e.target.value))
            }
            displayEmpty
            className={`${styles.select} ${
              variant === 'navbar'
                ? styles['select--navbar']
                : styles['select--standard']
            }`}
          >
            <MenuItem
              value=""
              disabled
              className={styles['menuItem--placeholder']}
            >
              <Typography variant="body2">Select Location</Typography>
            </MenuItem>
            {logic.departments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                <Box className={styles.departmentInfo}>
                  <Typography variant="body2" className={styles.departmentName}>
                    {department.name}
                  </Typography>
                  {department.description && (
                    <Typography
                      variant="caption"
                      className={styles.departmentDescription}
                    >
                      {department.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Typography
        sx={{
          mx: 1,
          fontSize: '1.5rem',
          fontWeight: 300,
          color: 'white',
        }}
      >
        |
      </Typography>
    </>
  )
}

export default DepartmentSelector
